/**
 * Frontend mirror of the curated marketplace tag pool.
 *
 * Source of truth: packages/workflow-templates/tag-pool.js
 *
 * Two reasons we mirror instead of importing directly:
 *   1. frontend/ does NOT depend on @zibby/workflow-templates — adding
 *      it would pull every template's source + scripts into the CRA
 *      build for one array of 12 strings.
 *   2. CRA's webpack resolver doesn't reach across the workspace's
 *      packages/ dir cleanly without aliasing every chunk.
 *
 * Mirror discipline: when you edit one file, edit the other. A vitest/
 * jest assertion lives at frontend/src/pages/MarketplacePage/__tests__/
 * marketplace-tag-pool.test.js — it reads the canonical file via fs +
 * compares the arrays. CI fails the diff if they drift.
 *
 * Pool order = display order in the chip row. Keep these arrays
 * identical (string-by-string, in order).
 */

export const MARKETPLACE_TAG_POOL = Object.freeze([
  'Chatbot',
  // Absorbed the old 'Code Review' entry (founder, 2026-08-20): implementing and
  // reviewing are one audience, and the split made a user hunt in two places.
  // Ordering INSIDE this section is declared per-template (`browseRank`) — see
  // browseRankCompare below.
  'Coding',
  'Design',
  'Testing',
  'On-call',
  'Bug Triage',
  'Incidents',
  'Notifications',
  'Reports',
  'AI Spend',
  'Cost Tracking',
  'Support',
  'Docs',
  'Onboarding',
  'Marketing',
  'Creative',
]);

export const MARKETPLACE_TAG_SET = new Set(MARKETPLACE_TAG_POOL);

/**
 * Helper used by the chip row — given a count map { tag → count },
 * return [{ name, count }] in POOL ORDER (not alphabetical, not
 * frequency). Tags not in the pool (e.g. legacy DDB rows, the
 * "child-workflow" meta-tag) are dropped from the display. Tags in
 * the pool but absent from current data render with count 0 — caller
 * decides whether to hide those.
 */
export function orderFacetsByPool(counts) {
  return MARKETPLACE_TAG_POOL
    .map((name) => ({ name, count: counts.get(name) || 0 }))
    .filter((f) => f.count > 0);
}

/**
 * DECLARED editorial rank of a card inside its section. The template writes
 * `marketplace.browseRank: <n>` and marketplace-sync-core's buildDesiredRow
 * copies it onto the row (null when undeclared), so this reads one row field
 * and knows nothing about any particular agent.
 *
 * Undeclared ⇒ Infinity ⇒ sorts after every ranked card, which is what keeps
 * this additive: a section where nothing declares a rank compares equal on this
 * key for every pair and falls through to the caller's existing tie-break,
 * unchanged. That is the whole reason ordering is a DECLARATION and not an
 * `if (slug === 'frontend-specialist')` in the renderer — the next agent that
 * should lead a section is a number in index.js, not an edit here.
 */
export function browseRankOf(entry) {
  const n = entry && entry.browseRank;
  return typeof n === 'number' && Number.isFinite(n) ? n : Infinity;
}

/**
 * Comparator fragment: use as the FIRST key of a section sort, then fall back
 * to whatever the surface already ordered by.
 *
 *   list.sort((a, b) => browseRankCompare(a, b) || <existing comparator>);
 */
export function browseRankCompare(a, b) {
  const ra = browseRankOf(a);
  const rb = browseRankOf(b);
  if (ra === rb) return 0;            // incl. Infinity vs Infinity → no opinion
  return ra < rb ? -1 : 1;
}

/**
 * Key + label of the trailing catch-all section. The key is a sentinel that can
 * never collide with a pool tag (pool entries are human labels like 'Coding'),
 * and it doubles as the scroll anchor id suffix (`mkt-group-__other__`).
 *
 * The self-host dashboard's catalog renderer uses the literal label "Other" for
 * the same bucket (selfhosted/ui/index.html, renderCatalog) — the two surfaces
 * share ZERO code, so the string is mirrored on purpose. Keep them equal.
 */
export const MARKETPLACE_OTHER_GROUP_KEY = '__other__';
export const MARKETPLACE_OTHER_GROUP_LABEL = 'Other';

/**
 * Sort key INSIDE a browse section: declared editorial rank first, then the
 * rating-first order this surface has always used.
 */
function compareWithinSection(a, b) {
  return browseRankCompare(a, b)
    || ((Number(b.ratingAvg) || 0) - (Number(a.ratingAvg) || 0))
    || ((Number(b.ratingCount) || 0) - (Number(a.ratingCount) || 0))
    || String(a.slug || '').localeCompare(String(b.slug || ''));
}

/**
 * Grouped-browse sections for the marketplace page.
 *
 * One section per pool facet, in pool order; every item lands in EXACTLY ONE
 * section — the FIRST facet (editorial pool order) it carries, i.e. its primary
 * category — followed by a trailing `Other` section holding everything that
 * matched no facet at all.
 *
 * WHY THE TRAILING BUCKET EXISTS. Before it, the loop ran over the facets and
 * returned; an item whose tags miss the curated pool rendered in NO section and
 * simply vanished from grouped browse — silently, with no empty state and no
 * console warning. That is a live hazard, not a hypothetical: the pool no longer
 * carries 'Code Review' while the prod marketplace rows for the two code-review
 * agents still do, and a dozen templates ship off-pool tags today
 * ('Knowledge Base', 'Integrations', 'Productivity', 'Prototyping', 'Frontend',
 * 'Memory'). With the bucket, the ORDER of a frontend deploy vs a marketplace
 * sync stops mattering — a card can be mis-tagged or ahead of the pool and it is
 * still reachable. The self-host dashboard has always behaved this way; this is
 * the cloud half of that parity.
 *
 * @param {Array<object>} items  marketplace rows (each may carry `tags`, `slug`)
 * @param {Array<{name:string,label:string}>} facets  pool facets, in pool order
 * @returns {Array<{key:string,label:string,subtitle:null,all:Array<object>}>}
 */
export function buildBrowseGroups(items, facets) {
  const list = Array.isArray(items) ? items : [];
  const out = [];
  // KIND sections LEAD the browse surface, before any topic section. A kind is
  // not a topic: 'fleet' is an orchestrator that installs a TEAM of member
  // agents, so it is not a peer of "Coding"/"Testing" and must not compete for
  // a tag slot. Partitioned on the row's DECLARED kind — the same rule the
  // self-host dashboard applies (its KIND_SECTIONS list), and the same closed
  // vocabulary the sync allowlists (marketplace-sync-core CARD_KINDS): a kind
  // absent from that allowlist never reaches a row, so this pass finds nothing
  // and the section simply does not appear.
  //
  // 'app' is deliberately NOT here: on cloud, hosted apps are a separate
  // surface (AppsMarketplaceSection) fed by a different source, so no app row
  // is ever in `items`. Self-host lists both from one array and therefore
  // partitions both kinds.
  const KIND_SECTIONS = [{ kind: 'fleet', label: 'Fleet' }];
  // `seen` tracks already-placed slugs so a multi-tag item (e.g. tags
  // [Reports, AI Spend, Cost Tracking]) doesn't repeat across every matching
  // section — and so the Other bucket below can be defined as "whatever this
  // same pass never placed" rather than a second, independently-written rule
  // about what counts as a pool tag (two places that must agree = a bug).
  const seen = new Set();
  for (const ks of KIND_SECTIONS) {
    const inKind = list.filter((it) => it && it.kind === ks.kind && !seen.has(it.slug));
    if (inKind.length === 0) continue;
    inKind.sort(compareWithinSection);
    inKind.forEach((it) => seen.add(it.slug));
    out.push({ key: ks.kind, label: ks.label, subtitle: null, all: inKind });
  }
  for (const facet of (Array.isArray(facets) ? facets : [])) {
    const inFacet = list.filter(
      (it) =>
        Array.isArray(it.tags) &&
        it.tags.includes(facet.name) &&
        !seen.has(it.slug)
    );
    if (inFacet.length === 0) continue;
    inFacet.sort(compareWithinSection);
    inFacet.forEach((it) => seen.add(it.slug));
    out.push({ key: facet.name, label: facet.label, subtitle: null, all: inFacet });
  }
  // Trailing catch-all. Membership is by the SAME `seen` set the loop filled, so
  // an item is here iff no section took it — never "both here and there".
  const other = list.filter((it) => !seen.has(it.slug));
  if (other.length) {
    other.sort(compareWithinSection);
    out.push({
      key: MARKETPLACE_OTHER_GROUP_KEY,
      label: MARKETPLACE_OTHER_GROUP_LABEL,
      subtitle: null,
      all: other,
    });
  }
  return out;
}
